import { useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { getApiBaseUrl } from '../../utils/routing';
import apiClient from '../../lib/api-client';

const API_URL = getApiBaseUrl().replace('/api/customer-portal', ''); // Get base URL without customer-portal path

interface SessionResponse {
  customerId: string;
  clientId: string;
  name: string;
  email: string;
  expiresAt: string;
}

export function TokenHandler() {
  const { merchantId } = useParams<{ merchantId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setToken, setCustomer, setLoading, setError, token: existingToken, customer } = useAuth();
  const hasVerified = useRef(false);

  console.log('[TokenHandler] Rendered, merchantId:', merchantId, 'existingToken:', existingToken ? 'present' : 'null');

  useEffect(() => {
    const verifyToken = async () => {
      const urlToken = searchParams.get('token');
      console.log('[TokenHandler] verifyToken called, urlToken:', urlToken ? urlToken.substring(0, 10) + '...' : 'null');

      // If there's a token in URL, ALWAYS verify it (even if we have cached data)
      if (urlToken && merchantId) {
        // Prevent duplicate verification of the same URL token
        if (hasVerified.current) {
          return;
        }
        console.log('Token found in URL:', urlToken);
        hasVerified.current = true;
        setLoading(true);
        setError(null);

        try {
          const response = await fetch(`${API_URL}/api/customer-portal/session/${urlToken}`);

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to authenticate');
          }

          const sessionData: SessionResponse = await response.json();
          console.log('[TokenHandler] Session data received:', sessionData);

          // Store token in api-client for subsequent requests
          console.log('[TokenHandler] Setting token in apiClient');
          apiClient.setToken(urlToken);

          // Transform session data to Customer format
          const nameParts = sessionData.name.split(' ');
          const customer = {
            id: sessionData.customerId,
            email: sessionData.email,
            firstName: nameParts[0] || '',
            lastName: nameParts.slice(1).join(' ') || '',
            phone: '',
            totalOrders: 0,
            totalSpent: 0,
          };

          // Store token, merchant ID, and customer
          setToken(urlToken, merchantId);
          setCustomer(customer);
          setLoading(false);

          // Remove token from URL but keep the page
          const currentPath = window.location.pathname;
          navigate(currentPath.replace(/\?.*$/, ''), { replace: true });
        } catch (error) {
          console.error('Token verification failed:', error);
          setError(error instanceof Error ? error.message : 'Authentication failed');
          setLoading(false);
        }
      } else if (existingToken && merchantId && !customer) {
        // Token exists in storage but no customer data, verify it's still valid
        console.log('Using stored token');
        hasVerified.current = true;
        setLoading(true);

        try {
          // Set token in api-client
          apiClient.setToken(existingToken);

          const response = await fetch(`${API_URL}/api/customer-portal/session/${existingToken}`);

          if (!response.ok) {
            throw new Error('Token expired or invalid');
          }

          const sessionData: SessionResponse = await response.json();

          // Transform session data to Customer format
          const nameParts = sessionData.name.split(' ');
          const customerData = {
            id: sessionData.customerId,
            email: sessionData.email,
            firstName: nameParts[0] || '',
            lastName: nameParts.slice(1).join(' ') || '',
            phone: '',
            totalOrders: 0,
            totalSpent: 0,
          };

          setCustomer(customerData);
          setLoading(false);
        } catch (error) {
          console.error('Stored token verification failed:', error);
          setError('Session expired. Please request a new login link.');
          setLoading(false);
        }
      } else if (existingToken && customer) {
        // Already have token and customer data, just ensure api-client has token
        apiClient.setToken(existingToken);
        setLoading(false);
      } else if (!existingToken) {
        // No token available
        setError('No authentication token provided. Please request a login link.');
        setLoading(false);
      }
    };

    verifyToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  return null; // This is a logic-only component
}
